import React, { useState, useEffect, useRef, useCallback } from "react";
import classnames from "classnames";
import { pathToRegexp } from "path-to-regexp";
import { useSummonerProfile, useProfileHistoricRanks } from "api/summonerProfileApi";
import { useQueryString } from "hooks/general-hooks";
import { useQuery } from "@apollo/client";
import SideAdsContainer from "components/SideAdsContainer";
import ErrorSummonerNotFound from "components/SummonerProfiles/ErrorSummonerNotFound";
import { Switch, Route, Redirect } from "react-router-dom";
import SummonerProfileContext from "components/SummonerProfiles/SummonerProfileContext";
import DefaultSummonerProfileSEO from "components/SummonerProfiles/views/ProfilesMain/DefaultSummonerProfileSEO";
import SummonerProfileHeader from "components/SummonerProfiles/views/ProfileHeader/SummonerProfileHeader";
import SummonerProfileNavContainer from "components/SummonerProfiles/views/ProfilesMain/SummonerProfileNavContainer";
import SummonerProfileBackground from "components/SummonerProfiles/views/ProfilesMain/SummonerProfileBackground";
import SummonerProfileFiltersContainer from "components/SummonerProfiles/views/ProfilesMain/SummonerProfileFiltersContainer";
import SummonerProfileOverview from "components/SummonerProfiles/views/ProfileOverview/SummonerProfileOverview";
import SummonerChampionStats from "components/SummonerProfiles/views/ProfileChampionStatsList/SummonerChampionStats";
import ProfileModalManager from "components/SummonerProfiles/views/ProfilesCustomization/ProfileModalManager";
import LiveGameMain from "components/SummonerProfiles/views/LiveGame/LiveGameMain";
import { GET_USER_STATE } from "api/graphql/queries";
import RouteStatus from "../../../RouteStatus";
import { decodeSummonerName } from "lib/profile-helpers";

import { SummonerProfileUpdateProvider } from "components/SummonerProfiles/SummonerProfileUpdateReducer";

const getPage = (pathname) => {
  const keys = [];
  const url = "/lol/profile/:region/:summonerName/:page?/:champion?/(.*)?";
  const regexp = pathToRegexp(url, keys, { end: false }).exec(pathname);
  const page = regexp[3];
  const champion = regexp[4];

  if (page === "champion-stats" && champion) {
    return "indiv-champion-stats";
  }

  return page;
};

const SummonerProfile = (props) => {
  const { location, history, match, previewMode } = props;
  const page = getPage(location.pathname);
  const [isModalOpen, setIsModalOpen] = useState(false);
  let { summonerName, region } = match.params;
  summonerName = decodeSummonerName(summonerName);

  const { data: userState } = useQuery(GET_USER_STATE, { ssr: false });
  const { lolSettings, premium } = (userState && userState.getState) || {};
  const { summoners } = lolSettings || {};
  const { summonerName: userSummonerName, verified } = (summoners && summoners[0]) || {};

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, [location.pathname]);

  const {
    data: historicData,
    loading: loadingHistoric,
    error: errorHistoric,
  } = useProfileHistoricRanks(summonerName, region, 420);
  const { loading, data, error } = useSummonerProfile(region, summonerName, {}, null, { notifyOnNetworkStatusChange: true });
  const { profileInit } = data || {};
  const { playerInfo } = profileInit || {};
  const { summonerName: realSummonerName } = playerInfo || {};

  if (loading || loadingHistoric) {
    return (
      <div className="profile-loader-container">
        <div className="spinthatshit-loader"></div>
      </div>
    );
  }

  if (!profileInit) {
    return (
      <RouteStatus statusCode={404}>
        <ErrorSummonerNotFound summonerName={summonerName} />
      </RouteStatus>
    );
  }

  const childProps = {
    page,
    premium,
    verified,
    summonerName,
    userSummonerName,
    region,
    profileInit: data.profileInit,
    lastModified: data.profileInit.lastModified,
    previewMode,
  };

  // TODO Slowly move "props" to context
  const contextValue = {
    page,
    previewMode,
    realSummonerName,
    summonerName,
    region,
    isSameSummoner: summonerName && userSummonerName && summonerName.toLowerCase() === userSummonerName.toLowerCase(),
    userSummonerName,
    userVerified: verified,
    premium,
    modalState: isModalOpen,
    setIsModalOpen,
  };

  return (
    <SummonerProfileUpdateProvider>
      <SummonerProfileContext.Provider value={contextValue}>
        <div className={classnames("summoner-profile-page")}>
          <div className="summoner-profile-container content-side-padding">
            {isModalOpen && (
              <ProfileModalManager
                summonerName={summonerName}
                regionId={region}
                setIsModalOpen={setIsModalOpen}
                modalState={isModalOpen}
              />
            )}
            <SummonerProfileBackground />
            <DefaultSummonerProfileSEO />
            <SummonerProfileHeader />
            <SummonerProfileNavContainer />
            <SummonerProfileFiltersContainer />
            <div className="summoner-profile_content-container">
              <Switch>
                <Route
                  path="/lol/profile/:region/:summonerName/overview"
                  render={(routerProps) => {
                    return <SummonerProfileOverview setProfileModalState={setIsModalOpen} {...childProps} />;
                  }}
                />
                <Route
                  path="/lol/profile/:region/:summonerName/champion-stats"
                  render={(routerProps) => <SummonerChampionStats {...childProps} />}
                />
                <Route
                  path="/lol/profile/:region/:summonerName/live-game"
                  render={(routerProps) => {
                    return <LiveGameMain {...routerProps} {...childProps} />;
                  }}
                />
                <Redirect to={`/lol/profile/${region}/${summonerName}/overview`} />
              </Switch>
            </div>
          </div>
          {!previewMode && !location.pathname.includes("patch-report") && <SideAdsContainer />}
        </div>
      </SummonerProfileContext.Provider>
    </SummonerProfileUpdateProvider>
  );
};

export default SummonerProfile;

// SummonerProfile.whyDidYouRender = true;
